import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingCart, ArrowLeft, X } from 'lucide-react';
import { useTheme } from '../../components/Theme/ThemeProvider';
import { displayNGNCurrency } from '../../helpers/displayCurrency';
import { summaryApi } from '../../common';
import { toast } from 'react-toastify';
import Context from 'context';
import addToCart from '../../helpers/addToCart';
import { authFetch } from '../../helpers/authFetch';

function LikedProducts() {
  const { colors } = useTheme();
  const { fetchUserAddToCart } = useContext(Context);
  const [likedProducts, setLikedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [removingProductId, setRemovingProductId] = useState(null);
  const [cleaningUp, setCleaningUp] = useState(false);


  const fetchLikedProducts = async () => {
  try {
    setLoading(true);

    const response = await authFetch(
      `${summaryApi.getUserLikedProducts?.url ?? 
        'https://josiesphones-server.onrender.com/api/likes/user-liked-products'
      }?limit=12&skip=${page * 12}`,
      { method: 'GET' }
    );

    if (!response.ok) {
      throw new Error(`Request failed: ${response.status}`);
    }

    const data = await response.json();

    if (data.success) {
      setLikedProducts(data.data);
    }

  } catch (error) {
    console.error('Error fetching liked products:', error);
    toast.error('Failed to load liked products');
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    fetchLikedProducts();
  }, [page]);

  const handleAddToCart = (e, productId) => {
    addToCart(e, productId, fetchUserAddToCart);
  }

  const handleRemoveFromLikes = async (e, productId) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      setRemovingProductId(productId);
      
      // FIX: Use the correct URL with productId parameter
      const unlikeUrl = typeof summaryApi.toggleLikeProduct.url === 'function' 
        ? summaryApi.toggleLikeProduct.url(productId)
        : `${summaryApi.toggleLikeProduct.url}/${productId}`;

      const response = await authFetch(summaryApi.toggleLikeProduct.url, {
  method: summaryApi.toggleLikeProduct.method,
  
  headers: {
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({ productId }) // Send productId in body
});

      const data = await response.json();

      if (data.success) {
        // Remove product from local state
        setLikedProducts(prev => prev.filter(product => product?._id !== productId));
        toast.success('Product removed from likes');
      } else {
        toast.error(data.message || 'Failed to remove product');
      }
    } catch (error) {
      console.error('Error removing product from likes:', error);
      toast.error('Failed to remove product from likes');
    } finally {
      setRemovingProductId(null);
    }
  };

  const handleCleanupOrphanedLikes = async () => {
  try {
    setCleaningUp(true);
    
    const response = await authFetch(
        `${summaryApi.cleanUpLikeProduct?.url || 'https://josiesphones-server.onrender.com/api/likes/cleanup-orphaned-likes'}?limit=12&skip=${page * 12}`,
        { method: 'DELETE' }
      );
    
    const data = await response.json();
    
    if (data.success) {
      toast.success(`Cleaned up ${data.deletedCount} orphaned likes`);
      // Refresh the list
      fetchLikedProducts();
    } else {
      toast.error(data.message || 'Cleanup failed');
    }
  } catch (error) {
    console.error('Error cleaning up orphaned likes:', error);
    toast.error('Cleanup failed');
  } finally {
    setCleaningUp(false);
  }
};


  return (
    <div className="min-h-screen pt-24" style={{ backgroundColor: colors.bg }}>
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/"
            className="p-2 rounded-lg transition-all hover:scale-110"
            style={{
              backgroundColor: colors.bgSecondary,
              color: colors.primary
            }}
          >
            <ArrowLeft size={24} />
          </Link>
          <div>
            <h1 className="text-4xl font-bold flex items-center gap-3" style={{ color: colors.text }}>
              <Heart size={32} fill="currentColor" style={{ color: colors.accent }} />
              Liked Products
            </h1>
            <p style={{ color: colors.textSecondary }}>{likedProducts?.length} products in your likes</p>
          </div>
        </div>
        <button
    onClick={handleCleanupOrphanedLikes}
    disabled={cleaningUp}
    className="px-4 py-2 rounded-lg text-white transition-all hover:scale-105 disabled:opacity-50"
    style={{
      backgroundColor: colors.accent,
    }}
  >
    {cleaningUp ? 'Cleaning...' : 'Clean Up'}
  </button>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, index) => (
              <div
                key={`loading-${index}`}
                className="rounded-xl overflow-hidden animate-pulse"
                style={{ backgroundColor: colors.bgSecondary }}
              >
                <div className="w-full h-56" style={{ backgroundColor: colors.bgTertiary }} />
                <div className="p-4 space-y-3">
                  <div className="h-4 rounded w-3/4" style={{ backgroundColor: colors.bgTertiary }} />
                  <div className="h-3 rounded w-1/2" style={{ backgroundColor: colors.bgTertiary }} />
                </div>
              </div>
            ))}
          </div>
        ) : likedProducts.length === 0 ? (
          <div
            className="text-center py-16 rounded-xl"
            style={{ backgroundColor: colors.bgSecondary }}
          >
            <Heart size={48} className="mx-auto mb-4" style={{ color: colors.textSecondary, opacity: 0.3 }} />
            <p style={{ color: colors.textSecondary }} className="text-lg">
              You haven't liked any products yet
            </p>
            <Link
              to="/"
              className="mt-6 inline-block px-6 py-3 rounded-lg text-white transition-all hover:scale-105 font-semibold"
              style={{
                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
              }}
            >
              Explore Products
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {likedProducts.map((product) => (
              <div
                key={`liked-${product?._id}`}
                className="rounded-xl overflow-hidden transition-all hover:shadow-2xl hover:scale-105 group relative"
                style={{
                  backgroundColor: colors.bgSecondary,
                  border: `1px solid ${colors.border}`
                }}
              >
                {/* Remove Button */}
                <button
                  onClick={(e) => handleRemoveFromLikes(e, product?._id)}
                  disabled={removingProductId === product?._id}
                  className="absolute top-3 right-3 z-10 p-2 rounded-full transition-all hover:scale-110 disabled:opacity-50"
                  style={{
                    backgroundColor: colors.bg,
                    color: colors.accent,
                    boxShadow: `0 2px 8px ${colors.shadow}`
                  }}
                >
                  {removingProductId === product?._id ? (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <X size={16} />
                  )}
                </button>

                <Link to={`/product/${product?._id}`}>
                  <div
                    className="relative w-full h-56 overflow-hidden"
                    style={{ backgroundColor: colors.bgTertiary }}
                  >
                    <img
                      src={product?.productImage?.[0]}
                      alt={product?.productName}
                      className="w-full h-full object-scale-down p-4 group-hover:scale-110 transition-transform duration-300"
                    />
                    <Heart
                      size={24}
                      fill="currentColor"
                      className="absolute top-3 left-3 transition-all"
                      style={{ color: colors.accent }}
                    />
                  </div>
                  
                  <div className="p-4 space-y-3">
                    <h3 className="font-bold line-clamp-2" style={{ color: colors.text }}>
                      {product?.productName}
                    </h3>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold" style={{ color: colors.primary }}>
                        {displayNGNCurrency(product?.productPrice)}
                      </span>
                      {product?.productDiscount && (
                        <span className="text-xs px-2 py-1 rounded" style={{
                          backgroundColor: colors.accent,
                          color: 'white'
                        }}>
                          -{Math.round((product?.productDiscount / product?.productPrice) * 100)}%
                        </span>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleAddToCart(e, product?._id);
                        toast.success('Added to cart');
                      }}
                      className="w-full py-2 rounded-lg font-semibold text-white transition-all hover:scale-105 flex items-center justify-center gap-2"
                      style={{
                        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
                      }}
                    >
                      <ShoppingCart size={16} />
                      Add to Cart
                    </button>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default LikedProducts;